import React, { Fragment, FunctionComponent, ReactNode, useState } from 'react';
import EligibilityPollComponent from '../../components/register/EligibilityPoll';
import postEligibilityCheckAsync from '../../api/eligibility/postEligibilityCheckAsync';
import postGuestEligibilityCheckAsync from '../../api/eligibility/postGuestEligibilityCheckAsync';
import getGuestEligibilityCheckAsync from '../../api/eligibility/getGuestEligibilityCheckAsync';
import postPatientCompleteAsync from '../../api/register/postPatientCompleteAsync';
import { AffiliationType, EligibilityCheck } from '../../interfaces/register';
import { CoverageCheckValues } from '../../interfaces/eligibility';
import { useRegistrationRouter } from '../../hooks';
import {
  ELIGIBILITY_ERROR_URL,
  ELIGIBILITY_LOGIN_URL,
} from '../../constants/register';
import { dependentChoiceLabel } from '../../utils/register/dependentChoice';
import {
  getCoverageData,
  isCoverageCheckFlow,
  setCoverageEligibility,
} from '../../lib/coverageCheckStore';
import {
  COVERAGE_BENEFITS_URL,
  COVERAGE_ELIGIBILITY_ERROR_URL,
  ELIGIBILITY_CHECK_TYPE_KEY,
  ELIGIBILITY_CHECK_RESULT_KEY,
  ELIGIBILITY_CHECK_TYPE_270_271,
  ELIGIBILITY_RESULT_ELIGIBLE,
  ELIGIBILITY_RESULT_NO_DATA,
  ELIGIBILITY_RESULT_FAILED,
  ELIGIBILITY_270_271_ERROR_LOCATION,
} from '../../constants/coverageCheck';

const fire270271 = (result: string, inCoverageFlow: boolean): void => {
  if (typeof window === 'undefined' || !inCoverageFlow) {
    return;
  }
  if (result === ELIGIBILITY_RESULT_ELIGIBLE) {
    if (window.createPageActionEvent) {
      const actionData: any = {
        controlName: '270-271-result',
        controlText: '270/271 eligibility result',
      };
      actionData.attributes = [
        {
          key: ELIGIBILITY_CHECK_TYPE_KEY,
          value: ELIGIBILITY_CHECK_TYPE_270_271,
        },
        { key: ELIGIBILITY_CHECK_RESULT_KEY, value: result },
      ];

      window.createPageActionEvent(actionData);
    }
    return;
  }

  if (window.createErrorEvent) {
    // `actionData: any` — same untyped-global idiom as above.
    const actionData: any = {
      controlName: '270-271-result',
      controlText: '270/271 eligibility result',
    };
    actionData.attributes = [
      {
        key: ELIGIBILITY_CHECK_TYPE_KEY,
        value: ELIGIBILITY_CHECK_TYPE_270_271,
      },
      { key: ELIGIBILITY_CHECK_RESULT_KEY, value: result },
    ];

    window.createErrorEvent(actionData, {
      errorLocation: ELIGIBILITY_270_271_ERROR_LOCATION,
      errorType: 'Verification-Failed',
      errorCode: result === ELIGIBILITY_RESULT_NO_DATA ? 'NO_DATA' : 'FAILED',
      errorCause: `270/271 eligibility ${result}`,
    });
  }
};

interface EligibilityPollProps {
  onSubmit(values: EligibilityCheck): void;
}

interface Props {
  onSubmit(): void;
  onComplete(): void;
  render(eligibilityPoll: EligibilityPollProps): ReactNode;
}

const EligibilityPoll: FunctionComponent<Props> = ({
  render,
  onComplete,
  onSubmit,
}: Props) => {
  const [eligibilityToken, setEligibilityToken] = useState<string>('');
  const { push, query } = useRegistrationRouter();
  const { affiliation } = query;
  const inCoverageFlow = isCoverageCheckFlow() && !affiliation;

  const submitEligibility = (
    values: EligibilityCheck
  ): ReturnType<typeof postEligibilityCheckAsync> => {
    if (inCoverageFlow) {
      const demographics = getCoverageData()?.values as
        | CoverageCheckValues
        | undefined;
      if (demographics) {
        return postGuestEligibilityCheckAsync(values, demographics);
      }
    }
    return postEligibilityCheckAsync(values);
  };

  const onEligibilitySubmit = (values: EligibilityCheck): Promise<void> => {
    return submitEligibility(values)
      .then(({ eligibilityCheck }) => {
        if (!eligibilityCheck) {
          fire270271(ELIGIBILITY_RESULT_FAILED, inCoverageFlow);
          return;
        }
        if (eligibilityCheck) {
          const { token, eligible, reason } = eligibilityCheck;

          if (token) {
            setEligibilityToken(token);
          }
          if (eligible) {
            if (typeof window !== 'undefined' && window.createPageActionEvent) {
              const actionData: any = {
                controlName: 'check-coverage-submit-success',
                controlText: 'Submit',
              };

              actionData.attributes = [
                {
                  key: 'dependentChoice',
                  value: dependentChoiceLabel(values.isDependent),
                },
              ];

              window.createPageActionEvent(actionData);
            }
          } else if (typeof window !== 'undefined' && window.createErrorEvent) {
            // Track unsuccessful eligibility check
            const actionData: any = {
              controlName: 'check-coverage-submit-attempt',
              controlText: 'Submit',
            };

            actionData.attributes = [
              {
                key: 'dependentChoice',
                value: dependentChoiceLabel(values.isDependent),
              },
            ];

            window.createErrorEvent(actionData, {
              errorLocation: 'Registration-CheckCoverage',
              errorType: 'Verification-Failed',
              errorCode: 'COVERAGE_NOT_VERIFIED',
              errorCause:
                reason ||
                'Coverage verification failed or patient is ineligible',
            });
          }
        }
      })
      .then(() => onSubmit())
      .catch((error) => {
        if (typeof window !== 'undefined' && window.createErrorEvent) {
          const actionData: any = {
            controlName: 'check-coverage-submit-attempt',
            controlText: 'Submit',
          };

          actionData.attributes = [
            {
              key: 'dependentChoice',
              value: dependentChoiceLabel(values.isDependent),
            },
          ];

          window.createErrorEvent(actionData, {
            errorLocation: 'Registration-CheckCoverage',
            errorType: 'Internal_API',
            errorCode: error?.code ?? 'API_ERROR',
            errorCause: error?.message ?? 'Failed to submit eligibility check',
          });
        }
        fire270271(ELIGIBILITY_RESULT_FAILED, inCoverageFlow);
      });
  };

  const handleEligible = (
    affiliationType: AffiliationType,
    affiliationId: number,
    existingAccount?: boolean
  ): Promise<any> => {
    if (inCoverageFlow) {
      if (eligibilityToken && affiliationId) {
        setCoverageEligibility({
          type: '270',
          token: eligibilityToken,
          affiliationId,
          existingAccount,
        });
      }
      fire270271(ELIGIBILITY_RESULT_ELIGIBLE, inCoverageFlow);
      return push(COVERAGE_BENEFITS_URL);
    }

    const actionData = {
      controlName: 'registration-success-response',
      controlText: 'Registration Success',
      controlType: 'Success',
      controlRegion: 'Body',
    };
    return postPatientCompleteAsync(affiliationType, affiliationId).then(
      ({ error, patient }) => {
        if (!error) {
          if (
            patient &&
            typeof window !== 'undefined' &&
            window.createPageActionEvent
          ) {
            // Push userInfo
            window.createPageActionEvent(actionData, {
              userID: patient.id ? patient.id.toString() : '',
              loggedInStatus: 'NOT_LOGGED_IN',
              userType: 'Internal',
            });
          }
          // Push actionEvent
          if (typeof window !== 'undefined' && window.createPageActionEvent) {
            window.createPageActionEvent(actionData);
          }
          push(ELIGIBILITY_LOGIN_URL);
        } else if (typeof window !== 'undefined' && window.createErrorEvent) {
          window.createErrorEvent(
            {
              controlName: 'registration-attempt',
              controlText: 'Registration Success',
            },
            {
              errorLocation: 'Registration-Submit',
              errorType: 'Internal_API',
              errorCode: '',
              errorCause: 'There was an unexpected error.',
            }
          );
        }
      }
    );
  };

  const handleIneligible = (eligibilityCheckId: string): Promise<any> => {
    fire270271(ELIGIBILITY_RESULT_NO_DATA, inCoverageFlow);
    const target = inCoverageFlow
      ? COVERAGE_ELIGIBILITY_ERROR_URL
      : ELIGIBILITY_ERROR_URL;
    return push(`${target}?eligibilityCheckId=${eligibilityCheckId}`);
  };

  return (
    <Fragment>
      {render({ onSubmit: onEligibilitySubmit })}

      {Boolean(eligibilityToken) && (
        <EligibilityPollComponent
          eligibilityToken={eligibilityToken}
          onEligible={handleEligible}
          onIneligible={handleIneligible}
          onComplete={onComplete}
          onFailed={() => fire270271(ELIGIBILITY_RESULT_FAILED, inCoverageFlow)}
          {...(inCoverageFlow
            ? { pollEligibilityCheck: getGuestEligibilityCheckAsync }
            : {})}
        />
      )}
    </Fragment>
  );
};

export default EligibilityPoll;
